/**
 * Tab "Fixation" (NPE) của màn Contract Detail — mirror Access
 * `frmProcurementQryDetailsNPETab3` (toolbar Edit/Delete/Download File/Print/Add Pay;
 * btnNew Access ẩn → không render) + `…Tab3_Fixation` (datasheet
 * `ProcurementPayQryFixation_NPE`, ColumnOrder explicit).
 */
import { Button } from '@mui/material'
import { useMemo, useState } from 'react'

import { useListContractFixationQuery, type FixationRow } from '../../store/api/contractDetailApi'
import ProcurementDatasheet, {
  type DatasheetColumn,
  type DatasheetRow,
} from './ProcurementDatasheet'

const FIXATION_COLUMNS: DatasheetColumn[] = [
  { field: 'cs_fixation_id', header: 'ID', kind: 'id', width: 64 },
  { field: 'fixation_date', header: 'Date', kind: 'date', width: 96, filter: 'date' },
  { field: 'fixation_no', header: 'Fixation No', kind: 'text', width: 118, freeze: true },
  { field: 'contract_no', header: 'Contract No', kind: 'text', width: 104 },
  { field: 'p_contract_no', header: 'P For Contract', kind: 'text', width: 106 },
  { field: 'cp_code', header: 'CP Code', kind: 'text', width: 84, filter: 'checkboxes' },
  { field: 'name', header: 'CP Name', kind: 'text', width: 170 },
  { field: 'quality_code', header: 'Qualty', kind: 'text', width: 76, filter: 'checkboxes' },
  { field: 'quantity_kg', header: 'Kg', kind: 'int', width: 84, sum: true },
  { field: 'price', header: 'Price', kind: 'int', width: 84 },
  { field: 'price_after_interest', header: 'Final Price (after Interest)', kind: 'int', width: 150 },
  { field: 'to_be_paid', header: 'P- TobeRequested (vnd)', kind: 'int', width: 148, sum: true },
  { field: 'total_amount', header: 'Total Amount', kind: 'int', width: 110, sum: true },
  { field: 'allocated_interest', header: 'Allocated Interest', kind: 'int', width: 122, sum: true },
  { field: 'allocated_amount', header: 'Allocated Advanced (incl Interest)', kind: 'int', width: 184, sum: true },
  { field: 'allocated_quantity', header: 'Allocated Quantity', kind: 'int', width: 122, sum: true },
  { field: 'p_paid', header: 'P - Requested (vnd)', kind: 'int', width: 128, sum: true },
  { field: 'note', header: 'Fixation Note', kind: 'text', width: 150 },
  { field: 'person_update', header: 'Updated Person', kind: 'text', width: 130 },
  { field: 'date_update', header: 'Updated Date', kind: 'text', width: 110 },
]

const toolbarBtnSx = {
  minWidth: 0,
  px: 1,
  py: 0.2,
  minHeight: 26,
  textTransform: 'none' as const,
  fontSize: '0.72rem',
  lineHeight: 1.1,
}

interface Props {
  contractNo: string
  canEdit: boolean
  onEdit: (row: FixationRow) => void
  onDelete: (row: FixationRow) => void
  onDownload: (row: FixationRow) => void
  onPrint: (row: FixationRow) => void
  onAddPay: (row: FixationRow) => void
}

export default function ContractDetailFixation({
  contractNo,
  canEdit,
  onEdit,
  onDelete,
  onDownload,
  onPrint,
  onAddPay,
}: Props) {
  const fixation = useListContractFixationQuery({ contractNo })
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const rows = useMemo<DatasheetRow[]>(
    () =>
      (fixation.data?.items ?? []).map((it, idx) => ({
        ...(it as Record<string, unknown>),
        id: `${it.cs_fixation_id ?? idx}`,
      })),
    [fixation.data?.items],
  )

  const selectedRow = useMemo(
    () => (fixation.data?.items ?? []).find((it) => `${it.cs_fixation_id}` === selectedId) ?? null,
    [fixation.data?.items, selectedId],
  )

  const needRow = (fn: (row: FixationRow) => void) => () => {
    if (selectedRow) fn(selectedRow)
  }

  return (
    <ProcurementDatasheet
      columns={FIXATION_COLUMNS}
      rows={rows}
      isLoading={fixation.isLoading}
      isFetching={fixation.isFetching}
      error={fixation.error}
      primaryField="cs_fixation_id"
      storageKey="procurement.contract-detail.fixation"
      searchPlaceholder="Search fixations…"
      errorTitle="Could not load fixations"
      emptyTitle="No fixations"
      emptyDescription="No rows for the selected contract."
      selectedRowId={selectedId}
      onRowSelect={(row) => setSelectedId(row ? row.id : null)}
      rightSlot={
        <>
          <Button size="small" variant="outlined" color="primary" sx={toolbarBtnSx} disabled={!canEdit || !selectedRow} onClick={needRow(onEdit)}>
            Edit
          </Button>
          <Button size="small" variant="outlined" color="error" sx={toolbarBtnSx} disabled={!canEdit || !selectedRow} onClick={needRow(onDelete)}>
            Delete
          </Button>
          <Button size="small" variant="outlined" color="primary" sx={toolbarBtnSx} disabled={!selectedRow} onClick={needRow(onDownload)}>
            Download File
          </Button>
          <Button size="small" variant="outlined" color="primary" sx={toolbarBtnSx} disabled={!selectedRow} onClick={needRow(onPrint)}>
            Print
          </Button>
          <Button size="small" variant="contained" color="primary" sx={toolbarBtnSx} disabled={!canEdit || !selectedRow} onClick={needRow(onAddPay)}>
            Add Pay
          </Button>
        </>
      }
    />
  )
}
